import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  IconButton,
  Alert,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  GetApp as InstallIcon,
  Close as CloseIcon,
  PhoneAndroid as AndroidIcon,
  Apple as AppleIcon,
  Laptop as DesktopIcon,
} from '@mui/icons-material';
import {
  canInstallPWA,
  showInstallPrompt,
  isPWA,
  isIOS,
  isAndroid,
  getInstallInstructions,
} from '../../utils/pwaUtils';

interface PWAInstallButtonProps {
  variant?: 'button' | 'banner';
  onInstalled?: () => void;
}

const PWAInstallButton: React.FC<PWAInstallButtonProps> = ({
  variant = 'button',
  onInstalled,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (isPWA()) {
      setIsInstalled(true);
      return undefined;
    }

    // Check if install prompt is available
    setShowInstallButton(canInstallPWA());

    // Listen for install prompt availability
    const handleInstallAvailable = () => {
      setShowInstallButton(true);
    };

    // Listen for app installed
    const handleInstalled = () => {
      setIsInstalled(true);
      setShowInstallButton(false);
      onInstalled?.();
    };

    window.addEventListener('pwa-install-available', handleInstallAvailable);
    window.addEventListener('pwa-installed', handleInstalled);

    return () => {
      window.removeEventListener('pwa-install-available', handleInstallAvailable);
      window.removeEventListener('pwa-installed', handleInstalled);
    };
  }, [onInstalled]);

  const handleInstallClick = async () => {
    // For iOS, show instructions
    if (isIOS()) {
      setShowInstructions(true);
      return;
    }

    // For Android and desktop, show native prompt
    const accepted = await showInstallPrompt();
    if (accepted) {
      setShowInstallButton(false);
    }
  };

  const handleCloseInstructions = () => {
    setShowInstructions(false);
  };

  // Don't show if already installed
  if (isInstalled) {
    return null;
  }

  // Don't show if install not available and not iOS
  if (!showInstallButton && !isIOS()) {
    return null;
  }

  const getDeviceIcon = () => {
    if (isIOS()) return <AppleIcon />;
    if (isAndroid()) return <AndroidIcon />;
    return <DesktopIcon />;
  };

  if (variant === 'banner') {
    return (
      <>
        <Alert
          severity="info"
          icon={<InstallIcon />}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleInstallClick}
              startIcon={getDeviceIcon()}
            >
              Install
            </Button>
          }
          sx={{
            mb: 2,
            borderRadius: 2,
            '& .MuiAlert-message': {
              width: '100%',
            },
          }}
        >
          <Typography variant="body2" fontWeight={500}>
            Install DesignOne App
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Get quick access from your home screen
          </Typography>
        </Alert>

        <Dialog
          open={showInstructions}
          onClose={handleCloseInstructions}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="h6">Install DesignOne App</Typography>
              <IconButton onClick={handleCloseInstructions} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box textAlign="center" py={2}>
              {getDeviceIcon()}
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                {isIOS() ? 'Install on iOS' : 'Install on Android'}
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {getInstallInstructions()}
              </Typography>
              {isIOS() && (
                <Box mt={3} textAlign="left">
                  <Typography variant="subtitle2" gutterBottom>
                    Steps:
                  </Typography>
                  <ol style={{ paddingLeft: 20 }}>
                    <li>
                      <Typography variant="body2">
                        Tap the Share button (square with arrow pointing up)
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2">
                        Scroll down and tap "Add to Home Screen"
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2">
                        Tap "Add" in the top right corner
                      </Typography>
                    </li>
                  </ol>
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseInstructions}>Got it</Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        startIcon={<InstallIcon />}
        onClick={handleInstallClick}
        sx={{
          borderRadius: 2,
          textTransform: 'none',
          fontWeight: 600,
        }}
      >
        Install App
      </Button>

      <Dialog
        open={showInstructions}
        onClose={handleCloseInstructions}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Install DesignOne App</Typography>
            <IconButton onClick={handleCloseInstructions} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box textAlign="center" py={2}>
            {getDeviceIcon()}
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              {isIOS() ? 'Install on iOS' : 'Install on Android'}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {getInstallInstructions()}
            </Typography>
            {isIOS() && (
              <Box mt={3} textAlign="left">
                <Typography variant="subtitle2" gutterBottom>
                  Steps:
                </Typography>
                <ol style={{ paddingLeft: 20 }}>
                  <li>
                    <Typography variant="body2">
                      Tap the Share button (square with arrow pointing up)
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      Scroll down and tap "Add to Home Screen"
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      Tap "Add" in the top right corner
                    </Typography>
                  </li>
                </ol>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInstructions}>Got it</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PWAInstallButton;
